import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Shared schemas ---

const webhookSubscriptionSchema = z.object({
  mailboxUpn: z.string(),
  subscriptionId: z.string(),
  expirationDateTime: z.string(),
  resource: z.string().optional(),
  clientState: z.string().nullish(),
  applicationId: z.string().optional(),
  changeType: z.string().optional(),
});

const webhookStatusSchema = z.object({
  isActive: z.boolean(),
  subscriptions: z.array(webhookSubscriptionSchema),
  lastChecked: z.date(),
});

const mailboxPermissionSchema = z.enum(["read", "write"]);

const mailboxSchema = z.object({
  id: z.uuid(),
  upn: z.string(),
  name: z.string().nullable(),
  enabled: z.boolean(),
  permission: mailboxPermissionSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

const emailIngestConfigSummarySchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  enabled: z.boolean(),
  tenantId: z.string().nullable(),
  clientId: z.string().nullable(),
  hasClientSecret: z.boolean(),
  clientSecretExpiresAt: z.string().nullable(),
  mailboxes: z.array(mailboxSchema),
  webhookStatus: webhookStatusSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// --- Contracts ---

const listContract = oc.output(z.array(emailIngestConfigSummarySchema)).route({
  method: "GET",
  summary: "List all email ingest configs for the organization",
});

const getContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(emailIngestConfigSummarySchema)
  .route({
    method: "GET",
    summary: "Get a single email ingest config with webhook status",
  });

const createContract = oc
  .input(
    z.object({
      name: z.string().min(1).max(255),
      tenantId: z.string(),
      clientId: z.string(),
      clientSecret: z.string(),
      clientSecretExpiresInDays: z.number().int().positive().nullish(),
    }),
  )
  .output(emailIngestConfigSummarySchema)
  .route({
    summary: "Create a new email ingest config",
  });

const updateContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      name: z.string().min(1).max(255).optional(),
      enabled: z.boolean().optional(),
    }),
  )
  .output(emailIngestConfigSummarySchema)
  .route({
    summary: "Update an email ingest config (name and enabled only)",
  });

const deleteContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Delete an email ingest config and its webhooks",
  });

const toggleUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
      enabled: z.boolean(),
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Toggle a mailbox UPN on/off",
  });

const addUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      upn: z.string().email(),
      name: z.string().trim().nullish(),
      permission: mailboxPermissionSchema,
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Add a mailbox UPN to a config",
  });

const updateUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
      upn: z.string().email().optional(),
      name: z.string().trim().nullish(),
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Update a mailbox's UPN and/or name",
  });

const removeUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
    }),
  )
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Remove a mailbox UPN from a config",
  });

const refreshWebhooksContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(webhookStatusSchema)
  .route({
    summary: "Force refresh all webhook subscriptions for a config",
  });

const refreshUpnContract = oc
  .input(z.object({ appRegistrationId: z.uuid(), mailboxId: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Refresh the webhook subscription for a single mailbox UPN",
  });

export const emailIngestConfigsContracts = {
  list: listContract,
  get: getContract,
  create: createContract,
  update: updateContract,
  delete: deleteContract,
  toggleUpn: toggleUpnContract,
  addUpn: addUpnContract,
  updateUpn: updateUpnContract,
  removeUpn: removeUpnContract,
  refreshWebhooks: refreshWebhooksContract,
  refreshUpn: refreshUpnContract,
};

export type EmailIngestConfigsContracts = typeof emailIngestConfigsContracts;
